/**
 * Fetches the Drive-side `SKJV/studies/skjv-studies-index.json` catalog so the
 * study list can show every study Brian has ever written (524+) without needing
 * to download each per-study envelope locally first.
 *
 * The polling loop in `study-index-state.ts` checks the file's `modifiedTime`
 * (cheap metadata GET) and only re-downloads the body when it actually changed,
 * keyed against the last value persisted in localStorage.
 */

import type { BibleLocation } from '@skjv/common';
import {
  findFileByAppProperty,
  findFolderByName,
  getFileText,
  getValidToken,
} from '@skjv/storage';

const STUDIES_SUBFOLDER = 'studies';
const INDEX_PROP_KEY = 'skjvIndex';
const INDEX_PROP_VALUE = 'studies-v1';

export interface StudyIndexEntry {
  studyId: string;
  name: string;
  reference: string;
  startingLocation: BibleLocation;
  endingLocation?: BibleLocation;
  studyGroupId: string;
  studyGroupName: string;
  students: string[];
  hasTeacherNotes: boolean;
  date: string;
  folderId: string;
}

export interface StudiesIndex {
  version: 1;
  generatedAt: string;
  studyCount: number;
  studies: StudyIndexEntry[];
}

export interface DriveFetchContext {
  clientId: string;
  scope: string;
  skjvFolderId: string;
}

export type IndexFetchResult =
  | { kind: 'unchanged' }
  | { kind: 'updated'; index: StudiesIndex; modifiedTime: string }
  | { kind: 'missing' }
  | { kind: 'no-auth' };

interface ResolvedFile {
  fileId: string;
  modifiedTime: string;
}

let cachedFileId: string | null = null;

export async function fetchStudyIndexIfChanged(
  ctx: DriveFetchContext,
  knownModifiedTime: string | null,
): Promise<IndexFetchResult> {
  const token = await getValidToken(ctx.clientId, ctx.scope);
  if (!token) return { kind: 'no-auth' };
  const resolved = await resolveIndexFile(token.accessToken, ctx.skjvFolderId);
  if (!resolved) return { kind: 'missing' };
  if (knownModifiedTime !== null && resolved.modifiedTime === knownModifiedTime) {
    return { kind: 'unchanged' };
  }
  const text = await getFileText(token.accessToken, resolved.fileId);
  const parsed = JSON.parse(text) as StudiesIndex;
  return { kind: 'updated', index: parsed, modifiedTime: resolved.modifiedTime };
}

async function resolveIndexFile(
  accessToken: string,
  skjvFolderId: string,
): Promise<ResolvedFile | null> {
  if (cachedFileId) {
    const metadata = await getFileMetadata(accessToken, cachedFileId);
    if (metadata) return { fileId: cachedFileId, modifiedTime: metadata.modifiedTime };
    cachedFileId = null;
  }
  const studiesFolderId = await findFolderByName(accessToken, STUDIES_SUBFOLDER, skjvFolderId);
  if (!studiesFolderId) return null;
  const file = await findFileByAppProperty(
    accessToken,
    studiesFolderId,
    INDEX_PROP_KEY,
    INDEX_PROP_VALUE,
  );
  if (!file) return null;
  const metadata = await getFileMetadata(accessToken, file.id);
  if (!metadata) return null;
  cachedFileId = file.id;
  return { fileId: file.id, modifiedTime: metadata.modifiedTime };
}

async function getFileMetadata(
  accessToken: string,
  fileId: string,
): Promise<{ modifiedTime: string } | null> {
  const url = `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?fields=modifiedTime`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
  if (!res.ok) return null;
  const data = (await res.json()) as { modifiedTime?: string };
  return data.modifiedTime ? { modifiedTime: data.modifiedTime } : null;
}

